import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail, BRAND } from "@/lib/api";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";

const Card = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] px-5">
    <div className="w-full max-w-md bg-white rounded-2xl p-10 shadow-xl">
      <div className="text-center mb-8">
        <div className="h-16 w-16 gh-octagon bg-black mx-auto flex items-center justify-center overflow-hidden ring-1 ring-[#D9CBA0]/50 mb-4">
          <img src={BRAND.logo} alt="Hope & Favor" className="h-full w-full object-contain" />
        </div>
        <h1 className="font-heading text-3xl text-[#3A3632]">Hope &amp; Favor</h1>
        <p className="text-sm text-[#8A8175] mt-1">Choose a new password</p>
      </div>
      {children}
    </div>
  </div>
);

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password reset — you can now sign in.");
      setTimeout(() => nav("/login"), 1800);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not reset password.");
    } finally { setBusy(false); }
  };

  if (!token) return (
    <Card>
      <div className="text-center" data-testid="reset-no-token">
        <p className="text-[#3A3632]">This reset link is missing or invalid.</p>
        <Link to="/forgot-password" className="block text-sm text-[#8E9A86] mt-6 hover:underline">Request a new reset link</Link>
      </div>
    </Card>
  );

  if (done) return (
    <Card>
      <div className="text-center" data-testid="reset-done">
        <div className="h-12 w-12 rounded-full bg-[#8E9A86]/12 mx-auto flex items-center justify-center mb-4"><CheckCircle2 className="h-6 w-6 text-[#8E9A86]" /></div>
        <p className="text-[#3A3632]">Your password has been reset. Redirecting you to sign in…</p>
        <Link to="/login" className="block text-sm text-[#8E9A86] mt-6 hover:underline">Go to sign in now</Link>
      </div>
    </Card>
  );

  return (
    <Card>
      <form onSubmit={submit} data-testid="reset-form" className="space-y-4">
        <div className="relative">
          <input type={showPw ? "text" : "password"} name="new-password" autoComplete="new-password" required placeholder="New password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reset-password" className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 pr-12 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
          <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="reset-toggle-visibility" aria-label={showPw ? "Hide password" : "Show password"} tabIndex={-1}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#8A8175] hover:text-[#8E9A86]">
            {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        <input type={showPw ? "text" : "password"} name="confirm-password" autoComplete="new-password" required placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
        {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="reset-submit" className="w-full bg-[#8E9A86] text-white rounded-full py-3 font-medium hover:bg-[#7A8772] transition-colors disabled:opacity-60">{busy ? "Saving…" : "Reset password"}</button>
      </form>
      <Link to="/login" className="block text-center text-sm text-[#8A8175] mt-5 hover:text-[#8E9A86]">← Back to sign in</Link>
    </Card>
  );
}
